<script lang="ts">
  import { useQuery } from '@sanity/svelte-loader';
  import { GetLandingPageQuery } from '$lib/sanity/queries/landing';
  import { env } from '$env/dynamic/public';
  import { GradientConfig } from './components/GradientStore';
  import { CurrentTrack, PlayState, STATE, bpm } from './components/RecordStore';
  import { onMount } from 'svelte';
  import { Canvas } from '@threlte/core';
  import { accessibilityPreferences } from '$lib/stores/accessibility';
  import type { PageData } from './$types';
  import type { LandingPage } from '$lib/sanity/schemas';
  import Scene from './components/three/Scene.svelte';
  import MusicPlayer from './components/MusicPlayer.svelte';
  import PrimaryNav from './components/PrimaryNav.svelte';
  import SecondaryNav from './components/SecondaryNav.svelte';
  import Legal from './components/Legal.svelte';
  import ControlsModal from '$components/Accessibility/Modal/ControlsModal.svelte';
  import Schema from '$components/StructuredData/Schema.svelte';
  import BreadcrumbListSchema from '$components/StructuredData/BreadcrumbListSchema.svelte';

  export let data: PageData;

  const query = useQuery<LandingPage>(GetLandingPageQuery, undefined, {
    initial: data.landingPageData
  });

  $: ({ data: page } = $query);

  //update when track / playpause changes
  $: if ($accessibilityPreferences.reduceColor) {
    bpm.set(data.config.defaultBPM);
    GradientConfig.set(data.config.a11yGradient);
  } else if ($PlayState == STATE.PLAYING && $CurrentTrack) {
    bpm.set($CurrentTrack.bpm);
    GradientConfig.set($CurrentTrack.visualConfig);
  } else {
    bpm.set(data.config.defaultBPM);
    GradientConfig.set(data.config.defaultGradient);
  }
  //initialize
  onMount(() => {
    if ($accessibilityPreferences.reduceColor) {
      GradientConfig.set(data.config.a11yGradient);
    } else {
      GradientConfig.set(data.config.defaultGradient);
    }
    bpm.set(data.config.defaultBPM);
  });
</script>

<svelte:head>
  <BreadcrumbListSchema items={[{ name: 'Block', path: '/' }]} />

  {#if page?.seoAttributes?.structuredData}
    <Schema input={page.seoAttributes.structuredData} pageUrlFallback={env.PUBLIC_BASE_URL} />
  {/if}
</svelte:head>

<div class="fullscreen">
  <div class="uiLayout">
    <div class="player">
      <MusicPlayer trackList={data.config.trackList} />
    </div>
    <div class="primaryNav">
      <PrimaryNav links={page.primaryLinks} sourceMap={data.sourceMap} path={['primaryLinks']} />
    </div>
    <div class="secondaryNav">
      <SecondaryNav
        links={page.secondaryLinks}
        sourceMap={data.sourceMap}
        path={['secondaryLinks']}
      />
    </div>
    <div class="legal">
      <Legal />
    </div>
  </div>
  <Canvas>
    <Scene />
  </Canvas>
</div>

<ControlsModal />

<style lang="scss">
  @import 'src/scss/variables';
  @import 'src/scss/landing.scss';

  h1 {
    color: white;
  }

  .fullscreen {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    background-color: var(--twist-color-gray-100);
    * {
      -ms-overflow-style: none;
    }
    ::-webkit-scrollbar {
      display: none;
    }
  }

  .uiLayout {
    @include disableClicks;
    @include fadeUI;
    position: fixed;
    width: 100vw;
    height: 100vh;
    height: var(--vh);
    display: grid;
    max-width: 360px;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99;
    padding: 24px;
    margin: 0 auto;
    grid-gap: 24px;
    grid-template-columns: auto;
    grid-template-rows: min-content min-content auto min-content min-content;
    grid-template-areas:
      'player'
      'primaryNav'
      '.'
      'secondaryNav'
      'legal';
    justify-items: center;
  }
  .player {
    grid-area: player;
    justify-self: stretch;
    align-self: start;
  }

  .primaryNav {
    margin-top: 16px;
    grid-area: primaryNav;
    justify-self: stretch;
    align-self: start;
  }

  .secondaryNav {
    grid-area: secondaryNav;
    justify-self: stretch;
    align-self: center;
  }

  .legal {
    grid-area: legal;
    justify-self: end;
    align-self: end;
  }

  @media screen and (min-width: 640px) {
    .uiLayer {
      grid-gap: 24px;
    }
  }

  @media screen and (min-width: 768px) {
    .uiLayout {
      padding: 40px;
      max-width: unset;
      grid-gap: 0;
      grid-template-columns: max-content auto max-content;
      grid-template-rows: min-content auto min-content;
      grid-template-areas:
        'player . primaryNav'
        '. . .'
        'secondaryNav . legal';
    }
    .player {
      justify-self: start;
    }
    .primaryNav {
      margin-top: 0;
      justify-self: end;
    }
    .secondaryNav {
      justify-self: start;
    }
  }
</style>
